// Progress bar per gli obiettivi mensili — componente gamification
import { CheckCircle2 } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Skeleton } from '../../../components/ui/Skeleton'

export interface MonthlyGoal {
  id: string
  label: string
  target: number
  current: number
  unit: string
  progressPercent: number
}

interface GoalProgressProps {
  goals: MonthlyGoal[]
  isLoading?: boolean
}

function GoalBar({ goal }: { goal: MonthlyGoal }) {
  const isCompleted = goal.progressPercent >= 100

  return (
    <div className="space-y-1.5" aria-label={`Obiettivo: ${goal.label}`}>
      {/* Header obiettivo */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5">
          {isCompleted && (
            <CheckCircle2
              className="h-4 w-4 text-green-500 shrink-0"
              aria-label="Obiettivo raggiunto"
            />
          )}
          <span className={isCompleted ? 'font-semibold text-green-600' : 'text-[var(--fc-foreground)]'}>
            {goal.label}
          </span>
        </div>
        <span className="text-xs font-medium text-[var(--fc-foreground-muted)]">
          {goal.current} / {goal.target} {goal.unit}
        </span>
      </div>

      {/* Barra progresso */}
      <div
        className="h-2.5 w-full rounded-full bg-[var(--fc-gray-200)]"
        role="progressbar"
        aria-valuenow={goal.progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${goal.progressPercent}% completato`}
      >
        <div
          className={[
            'h-2.5 rounded-full transition-all duration-500',
            isCompleted
              ? 'bg-green-500'
              : goal.progressPercent >= 70
              ? 'bg-[var(--fc-primary)]'
              : goal.progressPercent >= 40
              ? 'bg-yellow-400'
              : 'bg-[var(--fc-foreground-muted)]',
          ].join(' ')}
          style={{ width: `${Math.min(100, goal.progressPercent)}%` }}
        />
      </div>

      {/* Percentuale */}
      <p className="text-right text-xs text-[var(--fc-foreground-muted)]">
        {goal.progressPercent}%
      </p>
    </div>
  )
}

export function GoalProgress({ goals, isLoading = false }: GoalProgressProps) {
  if (isLoading) {
    return (
      <Card>
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between">
                <Skeleton width={140} height={16} />
                <Skeleton width={80} height={16} />
              </div>
              <Skeleton height={10} className="rounded-full" />
            </div>
          ))}
        </div>
      </Card>
    )
  }

  if (goals.length === 0) {
    return (
      <Card>
        <p className="text-center text-sm text-[var(--fc-foreground-muted)]">
          Nessun obiettivo mensile configurato.
        </p>
      </Card>
    )
  }

  const completedCount = goals.filter((g) => g.progressPercent >= 100).length

  return (
    <Card>
      {/* Riepilogo */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[var(--fc-foreground-muted)]">
          Obiettivi completati questo mese
        </p>
        <span className="rounded-full bg-[var(--fc-primary)] px-2.5 py-0.5 text-xs font-bold text-white">
          {completedCount} / {goals.length}
        </span>
      </div>

      {/* Lista obiettivi */}
      <div className="space-y-5" role="list" aria-label="Obiettivi mensili">
        {goals.map((goal) => (
          <div key={goal.id} role="listitem">
            <GoalBar goal={goal} />
          </div>
        ))}
      </div>
    </Card>
  )
}
